import type { TokenPair } from '@compass/client/types/auth';
import type { User } from '@compass/client/types/user';
import { useVerifyMfa } from '@hooks/useMfa';
import { getErrorMessage } from '@utils/errorMessages';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface MfaVerifyFormProps {
  challengeToken: string;
  methods: string[];
  onSuccess: (user: User, tokenPair: TokenPair) => void;
  onBack: () => void;
}

export function MfaVerifyForm({ challengeToken, methods, onSuccess, onBack }: MfaVerifyFormProps) {
  const [code, setCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const verifyMutation = useVerifyMfa();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    const method = useRecoveryCode ? 'recovery' : methods[0] || 'totp';

    verifyMutation.mutate(
      {
        mfa_challenge_token: challengeToken,
        method,
        code: trimmedCode,
      },
      {
        onSuccess: data => {
          if (data.user && data.token_pair) {
            onSuccess(data.user, data.token_pair);
          }
        },
      },
    );
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // For TOTP codes, only allow digits and limit to 6
    if (!useRecoveryCode) {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
      setCode(digitsOnly);
    } else {
      setCode(value);
    }
    // Reset error state when user types
    if (verifyMutation.isError) {
      verifyMutation.reset();
    }
  };

  const toggleRecoveryCode = () => {
    setUseRecoveryCode(prev => !prev);
    setCode('');
    verifyMutation.reset();
  };

  return (
    <motion.div
      key="mfa-verify"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="w-full max-w-md space-y-8"
    >
      <div className="space-y-2">
        <h2 className="text-3xl font-semibold">Two-Factor Authentication</h2>
        <p className="text-muted-foreground">
          {useRecoveryCode
            ? 'Enter one of your recovery codes to access your account.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          value={code}
          onChange={handleCodeChange}
          placeholder={useRecoveryCode ? 'Enter recovery code' : 'Enter 6-digit code'}
          type="text"
          inputMode={useRecoveryCode ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          autoFocus
          className={`w-full h-12 px-4 text-center text-lg tracking-widest ${
            verifyMutation.isError ? 'border-destructive focus-visible:ring-destructive' : ''
          }`}
        />

        {verifyMutation.isError && (
          <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Verification Failed</p>
                <p className="mt-1">
                  {getErrorMessage(verifyMutation.error) || 'Invalid code. Please try again.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-12"
          disabled={!code.trim() || verifyMutation.isPending}
        >
          {verifyMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </Button>

        <div className="flex items-center justify-between">
          <Button type="button" variant="link" className="h-auto p-0" onClick={onBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to login
          </Button>
          <Button type="button" variant="link" className="h-auto p-0" onClick={toggleRecoveryCode}>
            {useRecoveryCode ? 'Use authenticator code' : 'Use recovery code'}
          </Button>
        </div>
      </form>
    </motion.div>
  );
}
